import { createContext, useContext, useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useUser } from '@clerk/clerk-react';
import {
  saveStudy,
  loadStudy,
  saveUserProfile,
  loadUserProfile,
  saveGuide,
  loadGuide,
  saveDaysUnlocked,
  loadDaysUnlocked,
} from '../services/storage.js';
import { authenticatedFetch } from '../services/apiClient.js';
import { useSupabase } from '../hooks/useSupabase.js';

// Fire-and-forget cloud save. Doesn't await — localStorage is the
// source of truth in-session, Supabase is the durable backup.
// clerkId is supplied by AppContext so we can short-circuit when the
// user isn't signed in; the server actually identifies the user from
// the Authorization: Bearer token attached by authenticatedFetch.
function saveToCloud(type, data, clerkId) {
  if (!clerkId) return;
  try {
    authenticatedFetch('/api/save-data', {
      method: 'POST',
      body: JSON.stringify({ type, data }),
    }).catch((e) =>
      console.warn('[AppContext] Cloud save failed:', e?.message)
    );
  } catch (e) {
    console.warn('[AppContext] Cloud save sync error:', e?.message);
  }
}

const AppContext = createContext(null);

const initialUserProfile = {
  name: '',
  age: null,
  gender: '',
  enneagram_type: null,
  enneagram_wing: null,
  faith_background: '',
  life_season: '',
  primary_pain_point: '',
  focus_areas: [],
};

const initialGuide = {
  name: '',
  gender: '',
  tone: 'balanced',
};

const initialSubscription = {
  status: 'free_trial',
  trialStartedAt: null,
  trialEndsAt: null,
};

export function AppProvider({ children }) {
  // Clerk user (may be null during sign-out / on public routes). When
  // present, cloud-save side-effects fire from the wrapper setters.
  const { user, isLoaded, isSignedIn } = useUser();
  const clerkId = user?.id || null;
  // Set to true while restoring from /api/user-sync so the restore-
  // induced setters don't loop a cloud save right back to the cloud.
  const hydratingRef = useRef(false);
  // True while the initial cloud sync is in flight. Route guards block
  // their decision on this flag so a returning user's redirect can't
  // fire before profile/weeks/journal/highlights are restored.
  const [isSyncing, setIsSyncing] = useState(false);
  const hasSyncedRef = useRef(false);
  const { syncFromCloud } = useSupabase();

  // Persisted slots — hydrate from localStorage on first render, then
  // wrapper setters below mirror every change back to localStorage so a
  // user can close the app and return to their study on the same device.
  const [userProfile, setUserProfileState] = useState(
    () => loadUserProfile() || initialUserProfile
  );
  const [guide, setGuideState] = useState(() => loadGuide() || initialGuide);
  const [intakeMessages, setIntakeMessages] = useState([]);

  // Multi-week studies. Source of truth is `weeks`. `study` is kept as a
  // mirror of weeks[0] for any code that still reads the legacy single-
  // study shape.
  const [weeks, setWeeksState] = useState(() => {
    try {
      const savedWeeks = localStorage.getItem('rooted:v1:weeks');
      if (savedWeeks) return JSON.parse(savedWeeks);
      // Migrate legacy single-study record into a Week 1 entry.
      const legacy = loadStudy();
      if (legacy) return [{ weekNumber: 1, ...legacy }];
      return [];
    } catch {
      return [];
    }
  });
  const [study, setStudyState] = useState(() => {
    try {
      const savedWeeks = localStorage.getItem('rooted:v1:weeks');
      if (savedWeeks) {
        const parsed = JSON.parse(savedWeeks);
        return parsed.find((w) => w.weekNumber === 1) || parsed[0] || null;
      }
    } catch {}
    return loadStudy();
  });
  const [subscription, setSubscriptionState] = useState(() => {
    try {
      const saved = localStorage.getItem('rooted:v1:subscription');
      return saved ? JSON.parse(saved) : initialSubscription;
    } catch {
      return initialSubscription;
    }
  });
  const setSubscription = useCallback(
    (data) => {
      setSubscriptionState(data);
      try {
        localStorage.setItem('rooted:v1:subscription', JSON.stringify(data));
      } catch {}
      if (!hydratingRef.current) saveToCloud('subscription', data, clerkId);
    },
    [clerkId]
  );
  const [subscriptionTier, setSubscriptionTier] = useState('free_trial'); // 'free_trial' | 'standard' | 'premium'
  const [daysUnlocked, setDaysUnlockedState] = useState(() => loadDaysUnlocked());

  // Wrapper setters that auto-save. Exposed to the rest of the app under
  // the original names (setStudy, setUserProfile, etc.) so callers don't
  // need to change.
  // Save the weeks array to localStorage.
  const persistWeeks = useCallback((arr) => {
    try {
      localStorage.setItem('rooted:v1:weeks', JSON.stringify(arr));
    } catch {}
  }, []);

  // setStudy treats its argument as Week 1 — saves to legacy storage AND
  // merges into the weeks array.
  const setStudy = useCallback(
    (s) => {
      const writeWeek1 = (w1) => {
        setWeeksState((prevWeeks) => {
          const filtered = prevWeeks.filter((w) => w.weekNumber !== 1);
          const merged = [
            { weekNumber: 1, ...w1 },
            ...filtered,
          ].sort((a, b) => a.weekNumber - b.weekNumber);
          persistWeeks(merged);
          return merged;
        });
        if (!hydratingRef.current) {
          saveToCloud('week', { weekNumber: 1, ...w1 }, clerkId);
        }
      };

      if (typeof s === 'function') {
        setStudyState((prev) => {
          const next = s(prev);
          if (next) {
            saveStudy(next);
            writeWeek1(next);
          }
          return next;
        });
      } else {
        setStudyState(s);
        if (s) {
          saveStudy(s);
          writeWeek1(s);
        }
      }
    },
    [persistWeeks, clerkId]
  );

  const addWeek = useCallback(
    (weekNumber, weekData) => {
      if (!weekNumber) return;
      setWeeksState((prev) => {
        const filtered = prev.filter((w) => w.weekNumber !== weekNumber);
        const next = [...filtered, { weekNumber, ...weekData }].sort(
          (a, b) => a.weekNumber - b.weekNumber
        );
        persistWeeks(next);
        return next;
      });
      // Mirror Week 1 to legacy `study` so older code paths still see it.
      if (weekNumber === 1) {
        setStudyState(weekData);
        saveStudy(weekData);
      }
      if (!hydratingRef.current) {
        saveToCloud('week', { weekNumber, ...weekData }, clerkId);
      }
    },
    [persistWeeks, clerkId]
  );

  const setUserProfile = useCallback(
    (profile) => {
      if (typeof profile === 'function') {
        setUserProfileState((prev) => {
          const next = profile(prev);
          saveUserProfile(next);
          if (!hydratingRef.current)
            saveToCloud('profile', next, clerkId);
          return next;
        });
      } else {
        setUserProfileState(profile);
        saveUserProfile(profile);
        if (!hydratingRef.current)
          saveToCloud('profile', profile, clerkId);
      }
    },
    [clerkId]
  );

  const setGuide = useCallback((g) => {
    if (typeof g === 'function') {
      setGuideState((prev) => {
        const next = g(prev);
        saveGuide(next);
        return next;
      });
    } else {
      setGuideState(g);
      saveGuide(g);
    }
  }, []);

  const setDaysUnlocked = useCallback((d) => {
    if (typeof d === 'function') {
      setDaysUnlockedState((prev) => {
        const next = d(prev);
        saveDaysUnlocked(next);
        return next;
      });
    } else {
      setDaysUnlockedState(d);
      saveDaysUnlocked(d);
    }
  }, []);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [voiceMode, setVoiceMode] = useState(false); // hands-free conversation mode
  const [inputMode, setInputMode] = useState(null); // 'text' | 'voice' | null (not chosen)
  const [dailyFeedback, setDailyFeedback] = useState([]); // [{ dayNumber, summary, raw }]
  // Highlights — persisted across sessions (localStorage + cloud) so a
  // saved passage survives page refresh and follows the user to new
  // devices. Mirrors the journalEntries pattern.
  //
  // Records written before the "notes on highlights" change had no
  // `id` and no `note` field. Normalize defensively on hydration so
  // the rest of the app can assume the new shape unconditionally.
  // We write the normalized list back to localStorage in the same
  // initializer so the backfill persists past the first render.
  const [highlights, setHighlights] = useState(() => {
    try {
      const saved = localStorage.getItem('rooted:v1:highlights');
      if (!saved) return [];
      const parsed = JSON.parse(saved);
      if (!Array.isArray(parsed)) return [];
      let mutated = false;
      const normalized = parsed.map((h) => {
        const next = { ...h };
        if (!next.id) {
          next.id = crypto.randomUUID();
          mutated = true;
        }
        if (next.note === undefined) {
          next.note = null;
          mutated = true;
        }
        return next;
      });
      if (mutated) {
        try {
          localStorage.setItem(
            'rooted:v1:highlights',
            JSON.stringify(normalized)
          );
        } catch {}
      }
      return normalized;
    } catch {
      return [];
    }
  });

  // Journal — persisted across sessions
  const [journalEntries, setJournalEntriesState] = useState(() => {
    try {
      const saved = localStorage.getItem('rooted:v1:journal');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // Completed days — persisted full-day records that drive completion
  // status across the app and seed the future end-of-year book export.
  // Each entry contains the entire study content + the user's journal
  // entry + their check-in response, so this is the only data source
  // a future PDF generator needs to read.
  const [completedDays, setCompletedDaysState] = useState(() => {
    try {
      const saved = localStorage.getItem('rooted:v1:completedDays');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // Generated days — persisted parallel record of which days have been
  // produced by the model. The source of truth for "what's available
  // to read" is study.days; this list is a redundancy used for
  // progressive-generation bookkeeping and future analytics.
  const [generatedDays, setGeneratedDaysState] = useState(() => {
    try {
      const saved = localStorage.getItem('rooted:v1:generatedDays');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const updateUserProfile = useCallback(
    (patch) => {
      setUserProfile((prev) => ({ ...prev, ...patch }));
    },
    [setUserProfile]
  );

  const updateGuide = useCallback(
    (patch) => {
      setGuide((prev) => ({ ...prev, ...patch }));
    },
    [setGuide]
  );

  const appendIntakeMessage = useCallback((message) => {
    setIntakeMessages((prev) => [...prev, message]);
  }, []);

  const resetIntake = useCallback(() => {
    setIntakeMessages([]);
  }, []);

  const unlockNextDay = useCallback(() => {
    setDaysUnlocked((prev) => Math.min(prev + 1, 5));
  }, [setDaysUnlocked]);

  const toggleVoice = useCallback(() => {
    setVoiceEnabled((prev) => !prev);
  }, []);

  const appendDailyFeedback = useCallback(
    (entry) => {
      setDailyFeedback((prev) => [...prev, entry]);
      if (!hydratingRef.current) saveToCloud('feedback', entry, clerkId);
    },
    [clerkId]
  );

  const addHighlight = useCallback(
    (dayNumber, text) => {
      const record = {
        id: crypto.randomUUID(),
        dayNumber,
        text,
        note: null,
        createdAt: new Date().toISOString(),
      };
      setHighlights((prev) => {
        const next = [...prev, record];
        try {
          localStorage.setItem('rooted:v1:highlights', JSON.stringify(next));
        } catch {}
        return next;
      });
      if (!hydratingRef.current) saveToCloud('highlight', record, clerkId);
    },
    [clerkId]
  );

  // Update the note on an existing highlight. `note` may be null to
  // clear the note. State, localStorage, and cloud all mirror.
  const updateHighlightNote = useCallback(
    (id, note) => {
      setHighlights((prev) => {
        const next = prev.map((h) =>
          h.id === id ? { ...h, note } : h
        );
        try {
          localStorage.setItem(
            'rooted:v1:highlights',
            JSON.stringify(next)
          );
        } catch {}
        return next;
      });
      if (!hydratingRef.current) {
        saveToCloud('highlight_note', { id, note }, clerkId);
      }
    },
    [clerkId]
  );

  const getHighlights = useCallback(
    (dayNumber) => highlights.filter((h) => h.dayNumber === dayNumber),
    [highlights]
  );

  // Bulk-replace setters used by the StudyPlan first-mount syncFromCloud
  // path. Overwrite state + localStorage in lockstep so the local cache
  // is the cloud-derived array. Mirrors how setUserProfile / addWeek
  // handle localStorage from inside AppContext — the cloud-sync caller
  // doesn't need to know about storage keys. Intentionally cloud-as-
  // source-of-truth on sign-in: any local-only changes not yet synced
  // would be overwritten by cloud state, which is accepted per spec for
  // the minimum viable cross-device pass.
  const hydrateJournalEntries = useCallback((arr) => {
    if (!Array.isArray(arr)) return;
    setJournalEntriesState(arr);
    try {
      localStorage.setItem('rooted:v1:journal', JSON.stringify(arr));
    } catch {}
  }, []);

  const hydrateHighlights = useCallback((arr) => {
    if (!Array.isArray(arr)) return;
    setHighlights(arr);
    try {
      localStorage.setItem('rooted:v1:highlights', JSON.stringify(arr));
    } catch {}
  }, []);

  // First-mount-after-signin cloud sync. Pulls cloud → local state so
  // returning users restore profile / weeks / journal / highlights
  // BEFORE any route guard renders. Profile, journal, and highlights
  // are cloud-wins (mirrors the cross-device hydration policy). Weeks
  // stays conservative — only hydrate when local is empty — pending a
  // separate harmonization pass.
  useEffect(() => {
    if (!isLoaded || !isSignedIn || !user) return;
    if (hasSyncedRef.current) return;
    hasSyncedRef.current = true;

    setIsSyncing(true);
    (async () => {
      try {
        hydratingRef.current = true;
        const data = await syncFromCloud();
        if (!data) return;
        if (data.profile) setUserProfileState(data.profile);
        if (
          Array.isArray(data.weeks) &&
          data.weeks.length > 0 &&
          (weeks?.length || 0) === 0
        ) {
          for (const w of data.weeks) {
            addWeek(w.week_number, {
              ...w,
              weekNumber: w.week_number,
              days: w.days || [],
            });
          }
        }
        if (Array.isArray(data.journal)) hydrateJournalEntries(data.journal);
        if (Array.isArray(data.highlights)) hydrateHighlights(data.highlights);
        console.log(
          '[AppContext] Synced from cloud:',
          data.weeks?.length || 0,
          'weeks,',
          (data.journal || []).length,
          'journal_entries,',
          (data.highlights || []).length,
          'highlights'
        );
      } catch (err) {
        console.error('[AppContext] initial sync failed:', err);
      } finally {
        hydratingRef.current = false;
        setIsSyncing(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, isSignedIn, user?.id]);

  // Reset the one-shot gate on sign-out so re-signin re-fires the sync.
  useEffect(() => {
    if (!isSignedIn) hasSyncedRef.current = false;
  }, [isSignedIn]);

  const addJournalEntry = useCallback(
    (dayNumber, entry, weekNumber = 1) => {
      const createdAt = new Date().toISOString();
      const record = {
        dayNumber,
        weekNumber,
        entry,
        createdAt,
        id: Date.now(),
      };
      setJournalEntriesState((prev) => {
        const next = [...prev, record];
        try {
          localStorage.setItem('rooted:v1:journal', JSON.stringify(next));
        } catch {}
        return next;
      });
      if (!hydratingRef.current) saveToCloud('journal', record, clerkId);
    },
    [clerkId]
  );

  const getJournalEntries = useCallback(
    (dayNumber) => {
      if (dayNumber == null) return journalEntries;
      return journalEntries.filter((e) => e.dayNumber === dayNumber);
    },
    [journalEntries]
  );

  // markDayComplete — called from DayDetail when a check-in finishes.
  // Stores the full self-contained day record (study content + the
  // user's journal entry + check-in response + profile snapshot) so the
  // future end-of-year book generator can read this directly.
  const markDayComplete = useCallback(
    (dayData) => {
      const weekNumber = dayData.weekNumber || 1;
      const normalized = { ...dayData, weekNumber };
      let didInsert = false;
      setCompletedDaysState((prev) => {
        const exists = prev.find(
          (d) =>
            d.dayNumber === dayData.dayNumber &&
            (d.weekNumber || 1) === weekNumber
        );
        if (exists) return prev;
        didInsert = true;
        const next = [...prev, normalized];
        try {
          localStorage.setItem(
            'rooted:v1:completedDays',
            JSON.stringify(next)
          );
        } catch {}
        return next;
      });
      if (didInsert && !hydratingRef.current) {
        saveToCloud('completed_day', normalized, clerkId);
      }
    },
    [clerkId]
  );

  const isDayComplete = useCallback(
    (dayNumber, weekNumber = 1) =>
      completedDays.some(
        (d) =>
          d.dayNumber === dayNumber && (d.weekNumber || 1) === (weekNumber || 1)
      ),
    [completedDays]
  );

  const getWeek = useCallback(
    (weekNumber) =>
      weeks.find((w) => w.weekNumber === weekNumber) || null,
    [weeks]
  );

  // The "current" week is the lowest week 1..52 that doesn't have all
  // five days completed. Falls back to the highest available week if
  // somehow all weeks are full (shouldn't happen below 52).
  const getCurrentWeekNumber = useCallback(() => {
    const counts = {};
    for (const d of completedDays) {
      const wn = d.weekNumber || 1;
      counts[wn] = (counts[wn] || 0) + 1;
    }
    for (let w = 1; w <= 52; w += 1) {
      if (!counts[w] || counts[w] < 5) return w;
    }
    return weeks.length > 0
      ? Math.max(...weeks.map((w) => w.weekNumber))
      : 1;
  }, [completedDays, weeks]);

  // addGeneratedDay — store the new day in both the parallel
  // generatedDays log and merge it into study.days (sorted by day
  // number, dedupe by dayNumber).
  const addGeneratedDay = useCallback(
    (day) => {
      if (!day) return;
      const num = day.dayNumber || day.day_number;
      if (!num) return;
      const normalized = { ...day, dayNumber: num };

      setGeneratedDaysState((prev) => {
        const filtered = prev.filter((d) => d.dayNumber !== num);
        const next = [...filtered, normalized];
        try {
          localStorage.setItem(
            'rooted:v1:generatedDays',
            JSON.stringify(next)
          );
        } catch {}
        return next;
      });

      setStudy((prev) => {
        if (!prev) {
          return {
            week_title: `Week 1 — ${normalized.title || 'Your week'}`,
            week_theme: normalized.subtitle || '',
            opening_statement: '',
            days: [normalized],
            totalDays: 5,
          };
        }
        const existing = Array.isArray(prev.days) ? prev.days : [];
        const filtered = existing.filter(
          (d) => (d.dayNumber || d.day_number) !== num
        );
        const merged = [...filtered, normalized].sort(
          (a, b) =>
            (a.dayNumber || a.day_number) - (b.dayNumber || b.day_number)
        );
        return { ...prev, days: merged };
      });
    },
    [setStudy]
  );

  const getNextDayToGenerate = useCallback(() => {
    const days = study?.days || [];
    const have = days.map((d) => d.dayNumber || d.day_number);
    for (let i = 1; i <= 5; i += 1) {
      if (!have.includes(i)) return i;
    }
    return null;
  }, [study]);

  const value = useMemo(
    () => ({
      userProfile,
      setUserProfile,
      updateUserProfile,
      guide,
      setGuide,
      updateGuide,
      intakeMessages,
      setIntakeMessages,
      appendIntakeMessage,
      resetIntake,
      study,
      setStudy,
      subscription,
      setSubscription,
      subscriptionTier,
      setSubscriptionTier,
      daysUnlocked,
      setDaysUnlocked,
      unlockNextDay,
      voiceEnabled,
      setVoiceEnabled,
      toggleVoice,
      voiceMode,
      setVoiceMode,
      inputMode,
      setInputMode,
      dailyFeedback,
      setDailyFeedback,
      appendDailyFeedback,
      highlights,
      addHighlight,
      updateHighlightNote,
      hydrateHighlights,
      getHighlights,
      journalEntries,
      addJournalEntry,
      hydrateJournalEntries,
      getJournalEntries,
      completedDays,
      markDayComplete,
      isDayComplete,
      generatedDays,
      addGeneratedDay,
      getNextDayToGenerate,
      weeks,
      addWeek,
      getWeek,
      getCurrentWeekNumber,
      hydratingRef,
      isSyncing,
    }),
    [
      userProfile,
      guide,
      intakeMessages,
      study,
      weeks,
      addWeek,
      getWeek,
      getCurrentWeekNumber,
      subscription,
      subscriptionTier,
      daysUnlocked,
      voiceEnabled,
      voiceMode,
      inputMode,
      dailyFeedback,
      highlights,
      journalEntries,
      completedDays,
      generatedDays,
      addHighlight,
      updateHighlightNote,
      hydrateHighlights,
      getHighlights,
      addJournalEntry,
      hydrateJournalEntries,
      getJournalEntries,
      markDayComplete,
      isDayComplete,
      addGeneratedDay,
      getNextDayToGenerate,
      updateUserProfile,
      updateGuide,
      appendIntakeMessage,
      resetIntake,
      unlockNextDay,
      toggleVoice,
      appendDailyFeedback,
      isSyncing,
    ]
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used inside <AppProvider>');
  return ctx;
}

export default AppContext;
